/**
 * Auth feature-local constants (ADR-0009): error codes the UI branches on and the
 * client-facing policy numbers. Cross-cutting values (route paths) live in
 * `@/config/routes`; these are auth-specific and exported via the feature barrel.
 */

/** Stable error codes returned by the tracking auth realm (ADR-0008). Branch on these, never raw strings. */
export const AUTH_ERROR_CODES = {
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  ACCOUNT_TEMPORARILY_LOCKED: 'ACCOUNT_TEMPORARILY_LOCKED',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  INVALID_OTP: 'INVALID_OTP',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  RESEND_COOLDOWN_ACTIVE: 'RESEND_COOLDOWN_ACTIVE',
  INVALID_PHONE_NUMBER: 'INVALID_PHONE_NUMBER',
  CUSTOMER_NOT_FOUND: 'CUSTOMER_NOT_FOUND',
  /** QA-observed (US-112329): the deployed tracking backend sends this instead of the
   *  agreed `CUSTOMER_NOT_FOUND` (api.json) for `/auth/tracking/login` and
   *  `/auth/tracking/login/otp/send`. Treated as an alias everywhere CUSTOMER_NOT_FOUND
   *  is checked so the inline "not registered" field error still fires instead of falling
   *  through to the generic toast. */
  PHONE_NOT_REGISTERED: 'PHONE_NOT_REGISTERED',
  /** US-112329: FE-proposed, pending BE confirmation — not yet modeled in api.json's
   *  Tracking login examples. Name/status code chosen to match the ACCOUNT_DEACTIVATED
   *  precedent used elsewhere in the same Postman collection (story 110089). */
  ACCOUNT_DEACTIVATED: 'ACCOUNT_DEACTIVATED',
  INVALID_REFRESH_TOKEN: 'INVALID_REFRESH_TOKEN',
  RESET_TOKEN_EXPIRED: 'RESET_TOKEN_EXPIRED',
  PASSWORD_REUSED: 'PASSWORD_REUSED',
} as const;

export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[keyof typeof AUTH_ERROR_CODES];

/** Client-facing auth policy. Server is the source of truth; these drive UI defaults/timers. */
export const AUTH_POLICY = {
  /** Default OTP resend cooldown when the server doesn't supply one. */
  resendCooldownSeconds: 60,
  /** Max OTP resends per challenge. */
  maxResends: 5,
} as const;

/**
 * Copy for the green success toast every auth POST now raises (`<MutationToaster>`).
 *
 * Sign-out is deliberately absent: it lands the grower on the login screen, which announces itself,
 * and a *forced* sign-out is already narrated by `<AppShell>`'s force-logout toast.
 */
export const AUTH_SUCCESS_COPY = {
  SIGNED_IN: 'Signed in successfully.',
  CODE_SENT: 'Verification code sent.',
  CODE_RESENT: 'OTP has been resent successfully',
  CODE_VERIFIED: 'Code verified.',
  PASSWORD_RESET: 'Password reset successfully. Please log in.',
} as const;

/** US-117535 (grilling Q6): the AC's own logout copy, both ways — not the server's message. */
export const LOGOUT_COPY = {
  SUCCESS: "You've been logged out.",
  FAILED: "We couldn't log you out. Please try again.",
} as const;
